import React from "react";
import { Col, Label, Row } from "reactstrap";
import SelectOption from "Components/Co2Common/Fields/SelectOption";
import PricingCard from "pages/Co2Auth/Components/PricingCard";

export interface LicensePlanSelectorOption {
	key: string;
	label: string;
}

export interface LicensePlanSelectorPlan {
	key: string;
	title: string;
	price: string;
	period: string;
	periodNote?: React.ReactNode;
	features: string[];
	highlight?: boolean;
	ctaText?: string;
}

interface LicensePlanSelectorProps {
	contractOptions: LicensePlanSelectorOption[];
	selectedContractOption?: string;
	onSelectContractOption?: (value: string) => void;
	paymentCycleOptions: LicensePlanSelectorOption[];
	selectedPaymentCycleOption?: string;
	onSelectPaymentCycleOption?: (value: string) => void;
	plans: LicensePlanSelectorPlan[];
	onSelectPlan: (planKey: string) => void;
	readOnly?: boolean;
	topRightContent?: React.ReactNode;
	paymentCycleRightContent?: React.ReactNode;
	voucherContent?: React.ReactNode;
	plansRowClassName?: string;
}

const LicensePlanSelector: React.FC<LicensePlanSelectorProps> = ({
	contractOptions,
	selectedContractOption = "",
	onSelectContractOption,
	paymentCycleOptions,
	selectedPaymentCycleOption = "",
	onSelectPaymentCycleOption,
	plans,
	onSelectPlan,
	readOnly = false,
	topRightContent,
	paymentCycleRightContent,
	voucherContent,
	plansRowClassName = "g-3 mb-4",
}) => {
	const hasTopRow = (contractOptions.length > 1 && onSelectContractOption) || topRightContent;
	const paymentCycleAsideContent = voucherContent || paymentCycleRightContent;
	const hasPaymentCycleRow = (paymentCycleOptions.length > 1 && onSelectPaymentCycleOption) || paymentCycleAsideContent;

	return (
		<>
			{hasTopRow && (
				<div className="mb-4">
					<Row className="g-3 align-items-end justify-content-between">
						<Col md={6}>
							{contractOptions.length > 1 && onSelectContractOption && (
								<>
									<Label className="form-label text-body">
										<span className="fw-semibold">Contract type</span>
									</Label>
									<SelectOption
										selectedOption={selectedContractOption}
										setSelectedOption={onSelectContractOption}
										readOnly={readOnly}
										options={contractOptions}
									/>
								</>
							)}
						</Col>
						{topRightContent && (
							<Col md={6} className="d-flex justify-content-md-end">
								{topRightContent}
							</Col>
						)}
					</Row>
				</div>
			)}

			{hasPaymentCycleRow && (
				<div className="mb-3">
					<Row className="g-3 align-items-end justify-content-between">
						<Col md={6}>
							{paymentCycleOptions.length > 1 && onSelectPaymentCycleOption && (
								<>
									<Label className="form-label text-body">
										<span className="fw-semibold">Payment cycle</span>
									</Label>
									<SelectOption
										selectedOption={selectedPaymentCycleOption}
										setSelectedOption={onSelectPaymentCycleOption}
										readOnly={readOnly}
										options={paymentCycleOptions}
									/>
								</>
							)}
						</Col>
						{paymentCycleAsideContent && (
							<Col md={6}>
								{paymentCycleAsideContent}
							</Col>
						)}
					</Row>
				</div>
			)}

			{plans.length > 0 && (
				<Row className={plansRowClassName}>
					{plans.map((plan) => (
						<Col key={plan.key} xl={3} md={6} sm={12}>
							<div
								role="button"
								className="h-100"
								onClick={() => {
									if (!readOnly) onSelectPlan(plan.key);
								}}
								style={{ cursor: readOnly ? "default" : "pointer" }}
							>
								<PricingCard
									title={plan.title}
									price={plan.price}
									features={plan.features}
									period={plan.period}
									periodNote={plan.periodNote}
									onCtaClick={() => onSelectPlan(plan.key)}
									highlight={Boolean(plan.highlight)}
									ctaText={plan.ctaText || "Select"}
									disabled={readOnly}
								/>
							</div>
						</Col>
					))}
				</Row>
			)}
		</>
	);
};

export default LicensePlanSelector;
